import { useQuery } from '@tanstack/react-query'
import { useCallback } from 'react'
import { useNavigate } from 'react-router'

import { useErrorHandler } from '../../hooks/useErrorHandler'
import { usePageTitle } from '../../hooks/usePageTitle'
import { Category } from '../../models/Category'
import { RecordFactory } from '../../models/RecordFactory'
import { useOnboardingContext } from '../../providers/OnboardingProvider'
import { okResult } from '../../types/utils'
import { getEditionID } from '../../utils/users'
import { PSApplication } from '../app/PSApplication'
import { CategoriesPage, SelectableCategory } from '../items/CategoriesPage'
import { WorkflowSelectListSkeleton } from '../common/skeletons/WorkflowSelectListSkeleton'
import { useOnboardingBackHandler } from './useOnboardingBackHandler'

// helper function to display edition default categories while allowing user edits to show
const mergeCategories = (defaults: Category[], saved: Category[]): SelectableCategory[] => {
    return defaults.map((d) => {
        const foundFromSaved = saved.find((c) => c.editionCategory === d.id)
        if (foundFromSaved) {
            foundFromSaved.listings = d.listings // for display purposes only
            return { category: foundFromSaved, selected: true }
        }

        return { category: d, selected: false }
    })
}

export function SetupOnboardingCategoriesPage() {
    const me = PSApplication.me()
    const { advanceOnboardingState } = useOnboardingContext()
    const { logAndDisplayError } = useErrorHandler()
    const editionID = getEditionID(me)
    const navigate = useNavigate()

    usePageTitle('Categories')

    const onBack = useOnboardingBackHandler()

    const { data, isLoading } = useQuery({
        queryKey: ['onboarding', 'categories', editionID],
        queryFn: async () => {
            const [loadedEdition, loadedCategories] = await Promise.all([
                RecordFactory.getInstance().loadEdition(editionID),
                RecordFactory.getInstance().loadCategories(me.id!, ['service', 'product']),
            ])

            const categoriesToSelect = mergeCategories(
                loadedEdition?.defaultCategories ?? [],
                loadedCategories
            )

            return { edition: loadedEdition, categories: categoriesToSelect }
        },
    })

    const onNextHandler = useCallback(
        async (categories: Category[]) => {
            // Send the categories with the proper IDs set
            const clonedCategories = categories.map((c) => {
                const clone = c.clone()
                clone.listings = []
                clone.edition = editionID
                clone.editionCategory = c.id
                clone.id = undefined
                return clone
            })

            const result = await RecordFactory.getInstance().sendOnboardingCategories(
                clonedCategories,
                editionID,
                true
            )

            if (!result.ok) {
                logAndDisplayError(result.error)
                return result
            }

            await advanceOnboardingState(categories.flatMap((c) => c.listings))

            return okResult(null)
        },
        [editionID, advanceOnboardingState]
    )

    if (isLoading || !data) {
        return (
            <WorkflowSelectListSkeleton
                title="Categories"
                subtitle="This is a set of suggested categories to organize your services and products.
                    Select and customize them for your business"
                onClose={() => {}}
            />
        )
    }

    return (
        <CategoriesPage
            categories={data.categories}
            forOnboarding={true}
            onSave={onNextHandler}
            componentItems={[]}
            allowNextWithoutSelection={true}
            onBack={onBack}
            onClose={() => navigate('/')}
        />
    )
}
